import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Anchor } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useSiteImages } from "@/lib/siteImages";
import { useLang } from "@/lib/i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1771969703191-74381c286cac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxjYXJnbyUyMHNoaXAlMjBvY2VhbiUyMHR3aWxpZ2h0fGVufDB8fHx8MTc4ODIyNDEwMXww&ixlib=rb-4.1.0&q=85";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const si = useSiteImages();
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const LINES = [
    t.hero.l1,
    <>{t.hero.l2a}{" "}<span className="text-gold">{t.hero.l2b}</span></>,
    t.hero.l3,
  ];

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={si("hero", HERO_IMG)}
          alt="Cargo ship navigating open waters at dusk"
          className="w-full h-[120%] object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#060A14] to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full pt-36 pb-24">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="hero-eyebrow"
            className="text-xs md:text-sm uppercase tracking-[0.3em] text-gold font-semibold mb-8 flex items-center gap-3"
          >
            <span className="w-10 h-px bg-gold inline-block" />
            {t.hero.eyebrow}
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tight sm:tracking-tighter font-black text-[#F4F6F8] max-w-4xl leading-[1.08] sm:leading-[1.04]"
            style={{ textShadow: "0 2px 30px rgba(0,0,0,0.5)" }}
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span custom={i} variants={lineVariants} initial="hidden" animate="visible" className="block">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            data-testid="hero-subheading"
            className="mt-8 max-w-2xl text-base md:text-lg text-[#C6D0DD] leading-relaxed"
            style={{ textShadow: "0 1px 16px rgba(0,0,0,0.6)" }}
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-request-quote-btn"
              onClick={() => scrollToId("rfq")}
              className="inline-flex items-center gap-2 bg-gold text-midnight font-semibold px-8 py-4 rounded-full hover:bg-gold-light hover:text-midnight transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
            >
              {t.hero.cta1} <ArrowUpRight className="w-4 h-4" />
            </button>
            <button
              data-testid="hero-become-supplier-btn"
              onClick={() => scrollToId("suppliers")}
              className="inline-flex items-center gap-2 border border-white/30 text-[#F4F6F8] font-semibold px-8 py-4 rounded-full hover:border-gold hover:text-gold transition-[border-color,color,transform] duration-200 hover:-translate-y-0.5"
            >
              <Anchor className="w-4 h-4" /> {t.hero.cta2}
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="relative z-10 border-t border-white/10"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-wrap items-center justify-between gap-4">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#94A3B8]">
            {t.hero.strip}
          </p>
          <p className="text-[11px] uppercase tracking-[0.3em] text-gold animate-pulse">{t.hero.scroll}</p>
        </div>
      </motion.div>
    </section>
  );
};
